import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

interface SmartLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string
  className?: string
  children: React.ReactNode
  onLinkClick?: () => void
}

export default function SmartLink({ href, className, children, onLinkClick, ...props }: SmartLinkProps) {
  const location = useLocation()
  const navigate = useNavigate()

  const isExternal = href.startsWith('http') || href.startsWith('mailto:')
  const isHash = href.startsWith('#')
  const isInternalHash = href.startsWith('/#')

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onLinkClick) onLinkClick()

    if (isExternal) return // Let default behavior happen

    e.preventDefault()

    if (isHash || isInternalHash) {
      const hash = isInternalHash ? href.substring(1) : href

      // If we are on the home page, scroll to section
      if (location.pathname === '/') {
        window.history.pushState(null, '', hash)
        const element = document.querySelector(hash)
        element?.scrollIntoView({ behavior: 'smooth' })
      } else {
        // If we represent a "home section" link but are on another page, go home then scroll
        await navigate('/')
        // Small delay to allow home page to mount
        setTimeout(() => {
          const element = document.querySelector(hash)
          element?.scrollIntoView({ behavior: 'smooth' })
          window.history.replaceState(null, '', hash)
        }, 100)
      }
    } else {
      // Standard internal routing
      navigate(href)
      window.scrollTo(0, 0)
    }
  }

  return (
    <a
      href={href}
      className={className}
      onClick={handleClick}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      {...props}
    >
      {children}
    </a>
  )
}
